/**
 * URL-hash keys for the search feature's deep-linkable overlays, bound via
 * `useHashDialog` / `useHashParamDialog` (see `@/hooks/use-hash-dialog`).
 * Row-scoped overlays carry the entity id as a hash param (`#documents/5`).
 */
export const SEARCH_HASH = {
  /** `#search` — Cmd+K global search dialog (open-state owned by the app shell). */
  search: 'search',
  /** `#documents/<registrationId>` — ContainerDocumentsDialog drill-in. */
  documents: 'documents',
  /** `#viewer/<ocrResultId>` — OcrSearchViewerDialog. */
  viewer: 'viewer'
} as const;

export type SearchHashKey = (typeof SEARCH_HASH)[keyof typeof SEARCH_HASH];

/**
 * Document types carried on `page_index` hits (mirrors the backend
 * PageIndex::DOCUMENT_TYPES enum). Each maps to an i18n key `search.docType.*`.
 */
export const DOCUMENT_TYPES = [
  'letter',
  'memo',
  'report',
  'decree',
  'form',
  'contract',
  'invoice',
  'correspondence',
  'other'
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/**
 * Example natural-language queries surfaced as one-tap suggestion chips under
 * the search box (shown on the empty state). Each key resolves to
 * `search.suggestions.{key}`, whose translated value is BOTH the chip label and
 * the query submitted — so the phrase the model parses is localized too.
 */
export const SEARCH_SUGGESTION_KEYS = [
  'rejected',
  'inOcr',
  'closed',
  'notInBox'
] as const;

/**
 * Toolbar filter option lists for the smart-search page. Each entry pairs the
 * raw value sent to the backend with an i18n key under `search.filters.*`;
 * labels are resolved in the component so the bar stays translation-driven.
 */
export const STATUS_OPTIONS = [
  { value: 'active', labelKey: 'statusActive' },
  { value: 'closed', labelKey: 'statusClosed' }
] as const;

export const REJECTED_OPTIONS = [
  { value: 'yes', labelKey: 'rejectedYes' },
  { value: 'no', labelKey: 'rejectedNo' }
] as const;

export const BOX_MEMBERSHIP_OPTIONS = [
  { value: 'in', labelKey: 'inBox' },
  { value: 'out', labelKey: 'notInBox' }
] as const;

/**
 * Document-type filter options — reuses the {@link DOCUMENT_TYPES} list; labels
 * resolve via the existing `search.docType.*` keys.
 */
export const DOC_TYPE_OPTIONS = DOCUMENT_TYPES.map((value) => ({
  value,
  labelKey: value
}));
